import React, { useEffect, useRef, useState } from "react";

/**
 * The "Let's talk." concierge — a lightweight chat that stands in for an email address.
 * It POSTs the running transcript to /rest/chat (the WAR proxies DeepInfra, so the API key
 * never touches the browser) and shows the reply. Contact details are NOT collected here —
 * that's the deferred stage-2 form.
 */
export default function Concierge({ onClose }) {
  const [messages, setMessages] = useState([]); // { role, content, display? }
  const [input, setInput] = useState("");
  const [leadId, setLeadId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const logRef = useRef(null);

  const FALLBACK_GREETING =
    "Hey — tell me what you're trying to build and I'll point you to the right tier.";

  // Opening line comes from the config-driven greeting (GET /rest/chat). It's display-only:
  // it isn't sent back to the model as a conversation turn.
  useEffect(() => {
    let cancelled = false;
    fetch("/rest/chat")
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setMessages([{ role: "assistant", content: d.greeting || FALLBACK_GREETING, display: true }]);
      })
      .catch(() => {
        if (!cancelled) setMessages([{ role: "assistant", content: FALLBACK_GREETING, display: true }]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the newest message in view.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy]);

  // Escape closes the chat.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function send(e) {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    setError(null);

    // Send real turns only (drop the display-only greeting) so the transcript sent to the
    // model starts at the first user message.
    const payload = next
      .filter((m) => !m.display)
      .map(({ role, content }) => ({ role, content }));

    try {
      const res = await fetch("/rest/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId, messages: payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      if (data.leadId) setLeadId(data.leadId);
      setMessages((m) => [...m, { role: "assistant", content: data.message.content }]);
    } catch (err) {
      setError(err.message || "The concierge hit a snag — try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="concierge-overlay" onMouseDown={onClose}>
      <div
        className="concierge-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Chat with Newfangle Media"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="concierge-head">
          <span className="concierge-title">Newfangle Media</span>
          <span className="concierge-sub type-label">Front desk</span>
          <button type="button" className="concierge-close" onClick={onClose} aria-label="Close chat">
            &times;
          </button>
        </div>

        <div className="concierge-log" ref={logRef}>
          {messages.map((m, i) => (
            <div key={i} className={"bubble " + (m.role === "user" ? "from-user" : "from-bot")}>
              {m.content}
            </div>
          ))}
          {busy && (
            <div className="bubble from-bot typing" aria-label="Typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          {error && <div className="concierge-error">{error}</div>}
        </div>

        <form className="concierge-input" onSubmit={send}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell me what you need…"
            aria-label="Your message"
            autoFocus
            disabled={busy}
          />
          <button type="submit" disabled={busy || !input.trim()}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
